import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HelpCircle } from 'lucide-react';

import Footer from '../../components/projects/Footer';
import Header from '../../components/projects/Header';
import { useDashboardTabNavigation } from '../../components/projects/useDashboardTabNavigation';
import { useAppSelector } from '../../hooks';
import { getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { AnalyticsSubTab } from '../../types/analytics';
import { AnalyticsSidebar } from './components/AnalyticsSidebar';
import { ComingSoonTab } from './components/ComingSoonTab';
import { HelpPanel } from './components/HelpPanel';
import { LuminairePerformanceTab } from './components/LuminairePerformanceTab';
import { MyLuminairesTab } from './components/MyLuminairesTab';

const VALID_TABS: AnalyticsSubTab[] = [
  'my-luminaires',
  'luminaire-performance',
  'overview',
  'portfolio-gap',
  'regional',
  'lead-activity',
];

export const AnalyticsPage = () => {
  const user = useAppSelector(getUser);
  const handleHeaderTab = useDashboardTabNavigation('analytics');
  const [searchParams, setSearchParams] = useSearchParams();
  const [helpOpen, setHelpOpen] = useState(false);

  const tabParam = searchParams.get('tab') as AnalyticsSubTab | null;
  const activeTab: AnalyticsSubTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'my-luminaires';

  // Analytics is restricted to admins or users with the MANUFACTURER role.
  // Admin detection accepts the same shapes used elsewhere (e.g. ProjectsPage):
  // 'ADMIN' / 'ROLE_ADMIN' in roles[], or a top-level user.role string.
  const isAdmin =
    !!user?.roles?.includes('ADMIN') ||
    !!user?.roles?.includes('ROLE_ADMIN') ||
    user?.role === 'ADMIN' ||
    user?.role === 'ROLE_ADMIN';
  const isManufacturer = !!user?.roles?.includes('MANUFACTURER');
  const hasAnalyticsAccess = isAdmin || isManufacturer;

  useEffect(() => {
    if (!user) return;
    if (!hasAnalyticsAccess) {
      history.push('/forbidden');
    }
  }, [user, hasAnalyticsAccess]);

  if (!user || !hasAnalyticsAccess) {
    return null;
  }

  const handleSidebarTab = (tab: AnalyticsSubTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    next.delete('luminaireId');
    setSearchParams(next, { replace: true });
  };

  const handleOpenPerformance = (luminaireId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'luminaire-performance');
    next.set('luminaireId', luminaireId);
    setSearchParams(next, { replace: true });
  };

  const initialLuminaireId = searchParams.get('luminaireId');

  return (
    <div className="family-inter">
      <Header activeTab="analytics" onTabChange={handleHeaderTab} showAnalyticsTab={true} />

      <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 226px)' }}>
        <AnalyticsSidebar activeTab={activeTab} onTabChange={handleSidebarTab} />

        <div className="flex-1 flex flex-col overflow-hidden bg-black">
          {/* Section header inside content (date range, help) */}
          <div className="flex items-center justify-between border-b border-greyP4 px-4 md:px-6 py-3 bg-black">
            <div>
              <h1 className="text-white text-lg font-bold">
                {activeTab === 'my-luminaires' && 'My Luminaires'}
                {activeTab === 'luminaire-performance' && 'Luminaire Performance'}
                {activeTab === 'overview' && 'Portfolio Overview'}
                {activeTab === 'portfolio-gap' && 'Portfolio Gap'}
                {activeTab === 'regional' && 'Regional Insights'}
                {activeTab === 'lead-activity' && 'Lead Activity'}
              </h1>
              <p className="text-greyP2 text-xs mt-0.5">
                {activeTab === 'my-luminaires' &&
                  'Your full catalog with usage, favorites, and Market Match appearances.'}
                {activeTab === 'luminaire-performance' &&
                  'Compare two luminaires across compatibility, usage, and commercial metrics.'}
                {activeTab !== 'my-luminaires' &&
                  activeTab !== 'luminaire-performance' &&
                  'Coming soon — placeholder view.'}
              </p>
            </div>
            <button
              onClick={() => setHelpOpen(true)}
              title="Metric definitions"
              className="w-9 h-9 rounded-full border border-greyP4 bg-blackP1 hover:border-green01/50 flex items-center justify-center text-greyP2 hover:text-green01 transition-colors"
            >
              <HelpCircle size={16} />
            </button>
          </div>

          {activeTab === 'my-luminaires' && <MyLuminairesTab onOpenPerformance={handleOpenPerformance} />}
          {activeTab === 'luminaire-performance' && <LuminairePerformanceTab initialSlotAId={initialLuminaireId} />}
          {activeTab === 'overview' && (
            <ComingSoonTab
              title="Portfolio Overview"
              blurb="High-level KPIs across your entire catalog — total matches, applies, lead-to-quote conversion, and trend deltas vs the previous period."
            />
          )}
          {activeTab === 'portfolio-gap' && (
            <ComingSoonTab
              title="Portfolio Gap Analysis"
              blurb="Identify project types and regions where your catalog under-performs vs the optimal distribution. Surfaces concrete R&D and product-strategy opportunities."
            />
          )}
          {activeTab === 'regional' && (
            <ComingSoonTab
              title="Regional Insights"
              blurb="Heatmap of luminaire performance across countries and climate zones. Find your strongest regions and where to invest in localisation."
            />
          )}
          {activeTab === 'lead-activity' && (
            <ComingSoonTab
              title="Lead Activity"
              blurb="Inbound inquiry feed, conversion funnel, and per-luminaire lead breakdown. The commercial layer on top of the analytics layer."
            />
          )}
        </div>
      </div>

      <Footer projectPage={false} activeTab="analytics" />

      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};
